import { LinkBox } from '@flos/react-ui';
import { useHippoText } from '../../../hooks/useHippoText';
import CoverableDetailsTemplate from '../../../templates/CoverableDetailsTemplate';
import WizardPagetemplate from '../../../templates/WizardPagetemplate';
import { useState } from 'react';
import CallMeModal from '../../../shared_components/CallMeModal/CallMeModal';
import { useRecoilState, useRecoilValue } from 'recoil';
import { allCoverablesOnsaAtom } from '../../../recoil/atoms/allCoverablesOnsaAtom';
import { getOnsaCurrentProductSelector } from '../../../recoil/selectors/getOnsaCurrentProductSelector';
import { getCoverableIndex } from '../../../utils/coverablesUtil';
import type { ClaimsHistoryInformationToUpdateDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';
import { useSaveClaimsHistoryInformation } from '../../../hooks/useSaveClaimsHistoryInformation';
import { track } from '../../../utils/trackingUtil';

const PrhNumberOfClaimsStep = ({
    goNext,
    goPrevious
}: {
    goNext: () => void;
    goPrevious: () => void;
}): React.JSX.Element => {
    const { saveClaimsHistoryInformation } = useSaveClaimsHistoryInformation();
    const [coverables, setCoverables] = useRecoilState(allCoverablesOnsaAtom);
    const currentProduct = useRecoilValue(getOnsaCurrentProductSelector);
    const { flavourName, fixedId } = currentProduct;
    const { houses = [], holidayHomes = [] } = coverables;
    const coverableIndex =
        flavourName === 'Husforsikring'
            ? getCoverableIndex(houses, fixedId)
            : getCoverableIndex(holidayHomes, fixedId);
    const getHippoText = useHippoText();
    const [showCallMeModal, setShowCallMeModal] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const trackClaims = (claimsNumber: number): void => {
        const houseFlavour = flavourName === 'Husforsikring' ? 'house' : 'holidayHome';
        track(`${houseFlavour}CoverableInformationNumberOfClaims`, [
            {
                numberOfClaims: claimsNumber
            }
        ]);
    };

    const saveChaWithZeroClaims = (claimsNumber: number): void => {
        const claimQuestions: ClaimsHistoryInformationToUpdateDTO = {
            [flavourName === 'Husforsikring' ? 'house' : 'holidayHome']: {
                fixedId,
                numberOfClaims: claimsNumber
            },
            productToUpdate: flavourName
        };

        void saveClaimsHistoryInformation(claimQuestions).then(() => {
            trackClaims(claimsNumber);
            goNext();
            setIsLoading(false);
        });
    };

    const handleClick = (claimsNumber: number): void => {
        if (isLoading) return;
        setIsLoading(true);
        const contentCoverables = flavourName === 'Husforsikring' ? houses : holidayHomes;
        const updatedCoverables = [...contentCoverables];

        updatedCoverables[coverableIndex] = {
            ...updatedCoverables[coverableIndex],
            numberOfClaims: claimsNumber
        };

        setCoverables((prev) => ({
            ...prev,
            [flavourName === 'Husforsikring' ? 'houses' : 'holidayHomes']: updatedCoverables
        }));

        if (claimsNumber !== 0) {
            trackClaims(claimsNumber);
            goNext();
            setIsLoading(false);
            return;
        }

        saveChaWithZeroClaims(claimsNumber);
    };

    return (
        <WizardPagetemplate onPrevious={goPrevious} hideNext disablePrevious={isLoading}>
            <CoverableDetailsTemplate
                header={getHippoText('DSS-COVERABLE-INFORMATION-STEP-CLAIMS-HEADER')}
                subheader={getHippoText('DSS-COVERABLE-INFORMATION-STEP-CLAIMS-TEXT-PRH')}
            >
                <div className="u-display-flex u-flex-column u-flex-items-start u-gap-5">
                    <LinkBox
                        className="flos-box"
                        title={getHippoText('DSS-COVERABLE-INFORMATION-STEP-CLAIMS-0-CLAIMS')}
                        onClick={() => {
                            handleClick(0);
                        }}
                    />
                    <LinkBox
                        className="flos-box"
                        title={getHippoText('DSS-COVERABLE-INFORMATION-STEP-CLAIMS-1-CLAIM')}
                        onClick={() => {
                            handleClick(1);
                        }}
                    />
                    <LinkBox
                        className="flos-box"
                        title={getHippoText('DSS-COVERABLE-INFORMATION-STEP-CLAIMS-2-CLAIM')}
                        onClick={() => {
                            handleClick(2);
                        }}
                    />
                    <LinkBox
                        className="flos-box"
                        title={getHippoText('DSS-COVERABLE-INFORMATION-STEP-CLAIMS-3-CLAIM')}
                        onClick={() => {
                            handleClick(3);
                        }}
                    />
                    <LinkBox
                        className="flos-box"
                        title={getHippoText(
                            'DSS-COVERABLE-INFORMATION-STEP-CLAIMS-MORE-THAN-3-CLAIMS'
                        )}
                        onClick={() => {
                            setShowCallMeModal(true);
                        }}
                    />
                </div>
            </CoverableDetailsTemplate>
            {showCallMeModal && (
                <CallMeModal
                    activityCode="dss_callcha_requesttoo_many_claims"
                    showModal={showCallMeModal}
                    title={getHippoText('DSS-CALLME-MODAL-PRC-MULTI-PURPOSE-HEADER')}
                    description={getHippoText('DSS-CALLME-MODAL-PRC-MULTI-PURPOSE-SUBHEADER')}
                    closeModal={() => {
                        setShowCallMeModal(false);
                    }}
                />
            )}
        </WizardPagetemplate>
    );
};

export default PrhNumberOfClaimsStep;
